import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  BuildingStorefrontIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import {
  PageCta,
  PageHero,
  SectionHeading,
} from '../components/page-templates/SitePageTemplates';

const services = [
  {
    step: '01',
    icon: ShieldCheckIcon,
    title: 'Trade Compliance',
    description: 'IOR/EOR coverage, customs control, tax handling, and local regulatory readiness before goods move.',
    link: '/services/cross-border-compliance',
    features: ['IOR/EOR services', 'Customs brokerage', 'Duties and taxes handling', 'Regulatory support'],
  },
  {
    step: '02',
    icon: TruckIcon,
    title: 'Global Transport & Freight',
    description: 'Secure, insured multi-modal transport for high-value ICT hardware across time-sensitive routes.',
    link: '/services/global-transport-freight',
    features: ['Ocean and air freight', 'IATA and FIATA licensed', 'USD 3M cargo insurance', 'Route orchestration'],
  },
  {
    step: '03',
    icon: BuildingStorefrontIcon,
    title: 'Warehousing & Fulfilment',
    description: 'Bonded warehousing in Singapore and Jebel Ali, inventory positioning, and RMA workflows across certified partner facilities.',
    link: '/services/warehousing-fulfilment',
    features: ['12,000 sq ft Changi hub', 'Global partner network', 'Real-time inventory', 'RMA management'],
  },
];

const operatingSequence = [
  'Validate market entry before assets ship.',
  'Move inventory with insured, accountable routing.',
  'Position stock in bonded warehouses next to the airport for fast release.',
  'Deliver, recover, and report under one operating layer.',
];

const ServicesPage: React.FC = () => {
  return (
    <div className="bg-white">
      <PageHero
        eyebrow="End-to-End Solutions"
        title="One partner for global ICT logistics."
        highlight="global ICT logistics."
        description="TrueLog connects trade compliance, global freight, and warehousing into one accountable execution model across 140+ countries."
        heroIcon={GlobeAltIcon}
        accentIcon={ShieldCheckIcon}
        actions={[
          { href: '/contact-us', label: 'Talk to experts' },
          { href: '/global-coverage', label: 'Explore coverage', variant: 'secondary' },
        ]}
        badges={[
          'Compliance before movement',
          'Fast bonded release',
          'Visibility through returns',
        ]}
        metrics={[
          { value: '3', label: 'Integrated service lines' },
          { value: '140+', label: 'Countries supported' },
          { value: '24/7', label: 'Control tower oversight' },
          { value: '1', label: 'Accountable execution partner' },
        ]}
        panelEyebrow="Execution architecture"
        panelTitle="How the operating model fits together"
        panelDescription="Each service line is built to remove a specific point of friction, but the value comes from how they connect."
        panelItems={services.slice(0, 3).map((service) => ({
          icon: service.icon,
          title: service.title,
          description: service.description,
        }))}
      />

      <section className="bg-white py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Service Stack"
            title="Three connected capabilities, one execution layer"
            description="The model is designed so your team does not have to stitch together separate forwarders, warehouses, and compliance providers."
          />

          <div className="grid gap-6 lg:grid-cols-3">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.06 }}
                className="group rounded-[30px] border border-slate-200/80 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.3)]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="inline-flex rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-3 text-white">
                    <service.icon className="h-6 w-6" />
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    {service.step}
                  </span>
                </div>

                <h3 className="mt-5 text-2xl font-semibold text-slate-950">{service.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{service.description}</p>

                <ul className="mt-5 space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm leading-7 text-slate-600">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-cyan-400" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={service.link}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2447d8] transition-colors hover:text-[#0E9ED5]"
                >
                  Explore service
                  <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom">
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div>
              <SectionHeading
                eyebrow="Operating Flow"
                title="Designed to keep execution calm as scope grows"
                description="The handoffs are intentional. Compliance informs routing, routing informs positioning, positioning informs delivery, and every movement feeds reporting."
                align="left"
              />
            </div>

            <div className="grid gap-4">
              {operatingSequence.map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.08 }}
                  className="flex items-start gap-4 rounded-[26px] border border-slate-200/80 bg-white p-5 shadow-[0_20px_45px_-36px_rgba(15,23,42,0.28)]"
                >
                  <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-bold text-white">
                    0{index + 1}
                  </span>
                  <p className="text-sm leading-7 text-slate-600">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PageCta
        title="Need a service mix built around your rollout model?"
        description="Share the countries, timelines, compliance constraints, and delivery standards you need to support. We’ll shape the service architecture around that reality."
        pills={services.slice(0, 3).map((service) => service.title)}
        secondaryHref="/why-truelog"
        secondaryLabel="Why TrueLog"
      />
    </div>
  );
};

export default ServicesPage;
